import { jsPDF } from 'jspdf';

// ─── DATOS DEL JARDÍN ────────────────────────────────────────────────────────
const JARDIN = {
    nombre: 'Jardín Infantil Campestre Moraleja',
    nit: '1038409684',
    telefono: '3146939016',
    correo: 'danielamoraleja2016@hotmail.com',
};

// ─── PALETA DE COLORES ────────────────────────────────────────────────────────
const COLOR = {
    verde: [34, 139, 96],    // Verde jardín principal
    verdeClaro: [209, 240, 224],  // Fondo sección verde claro
    grisOscuro: [40, 40, 40],   // Texto principal
    gris: [110, 110, 110],  // Texto secundario
    grisLinea: [220, 220, 220],  // Líneas separadoras
    blanco: [255, 255, 255],
    amarillo: [255, 193, 7],    // Acento fecha vencimiento
};

/**
 * Genera un PDF de factura / recibo de pago para el jardín.
 *
 * @param {object} pago - Documento de Firestore del pago recibido.
 * @param {object} dataNino - Datos del niño desde la colección "niños".
 * @param {Date}   nuevaFechaObj - Objeto Date con la nueva fecha de vencimiento.
 * @param {number} diasAprobados - Número de días aprobados en este pago.
 * @returns {Blob} PDF generado como Blob para subir a Storage.
 */
export const generarFactura = (pago, dataNino, nuevaFechaObj, diasAprobados) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const W = doc.internal.pageSize.getWidth();   // 210mm
    const MARGIN = 18;
    const COL_RIGHT = W - MARGIN;

    // ─── HELPERS ─────────────────────────────────────────────────────────────
    const setFont = (style = 'normal', size = 10, color = COLOR.grisOscuro) => {
        doc.setFont('helvetica', style);
        doc.setFontSize(size);
        doc.setTextColor(...color);
    };

    const line = (y, color = COLOR.grisLinea) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(0.3);
        doc.line(MARGIN, y, COL_RIGHT, y);
    };

    const rect = (x, y, w, h, fillColor, cornerRadius = 0) => {
        doc.setFillColor(...fillColor);
        doc.roundedRect(x, y, w, h, cornerRadius, cornerRadius, 'F');
    };

    // ─── CABECERA VERDE ───────────────────────────────────────────────────────
    rect(0, 0, W, 48, COLOR.verde);

    // Logo (si existe en public)
    try {
        doc.addImage('/logo.jpg', 'JPEG', MARGIN, 8, 28, 28, undefined, 'FAST');
    } catch (_) { /* logo opcional */ }

    setFont('bold', 16, COLOR.blanco);
    doc.text(JARDIN.nombre, MARGIN + 32, 18);

    setFont('normal', 8.5, [200, 235, 215]);
    doc.text(`RUT: ${JARDIN.nit}`, MARGIN + 32, 25);
    doc.text(`Tel: ${JARDIN.telefono}  ·  ${JARDIN.correo}`, MARGIN + 32, 31);

    // Etiqueta RECIBO en esquina superior derecha del header
    setFont('bold', 8, COLOR.blanco);
    doc.text('RECIBO DE PAGO', COL_RIGHT, 13, { align: 'right' });
    setFont('bold', 22, COLOR.blanco);
    doc.text(`#${pago.id.slice(-6).toUpperCase()}`, COL_RIGHT, 25, { align: 'right' });

    const fechaEmision = new Date(pago.fechaEnvio).toLocaleDateString('es-CO', {
        day: 'numeric', month: 'long', year: 'numeric'
    });
    setFont('normal', 8, [200, 235, 215]);
    doc.text(`Fecha de emisión: ${fechaEmision}`, COL_RIGHT, 33, { align: 'right' });

    // ─── SECCIÓN ACUDIENTE Y ESTUDIANTE ───────────────────────────────────────
    let y = 60;

    // Tarjeta izquierda — Acudiente
    rect(MARGIN, y, 82, 30, COLOR.verdeClaro, 4);
    setFont('bold', 7.5, COLOR.verde);
    doc.text('ACUDIENTE / RESPONSABLE', MARGIN + 5, y + 7);
    setFont('bold', 10, COLOR.grisOscuro);
    doc.text(dataNino.acudientes?.[0]?.nombre || pago.acudienteNombre || '-', MARGIN + 5, y + 15);
    setFont('normal', 9, COLOR.gris);
    doc.text(`Tel: ${dataNino.acudientes?.[0]?.telefono || '-'}`, MARGIN + 5, y + 22);

    // Tarjeta derecha — Estudiante
    rect(MARGIN + 88, y, 82, 30, [235, 248, 255], 4);
    setFont('bold', 7.5, [30, 120, 200]);
    doc.text('ESTUDIANTE', MARGIN + 93, y + 7);
    setFont('bold', 10, COLOR.grisOscuro);
    doc.text(dataNino.nombre || pago.ninoNombre || '-', MARGIN + 93, y + 15);
    setFont('normal', 9, COLOR.gris);
    const nivelText = dataNino.nivel || dataNino.grado || 'Jardín Infantil';
    doc.text(nivelText, MARGIN + 93, y + 22);

    // ─── TABLA DE CONCEPTO ────────────────────────────────────────────────────
    y += 42;

    // Encabezado tabla
    rect(MARGIN, y, COL_RIGHT - MARGIN, 9, COLOR.verde, 3);
    setFont('bold', 8, COLOR.blanco);
    doc.text('CONCEPTO', MARGIN + 5, y + 6);
    doc.text('DÍAS', MARGIN + 100, y + 6, { align: 'center' });
    doc.text('VALOR', COL_RIGHT - 5, y + 6, { align: 'right' });

    y += 9;

    // Fila 1 — Mensualidad
    rect(MARGIN, y, COL_RIGHT - MARGIN, 10, [248, 250, 252]);
    setFont('normal', 9, COLOR.grisOscuro);
    doc.text('Mensualidad Jardín Infantil Campestre Moraleja', MARGIN + 5, y + 7);
    doc.text(`${diasAprobados} días`, MARGIN + 100, y + 7, { align: 'center' });
    setFont('bold', 9, COLOR.grisOscuro);
    doc.text(`$${Number(pago.monto).toLocaleString('es-CO')}`, COL_RIGHT - 5, y + 7, { align: 'right' });

    y += 10;
    line(y);

    // ─── TOTALES ─────────────────────────────────────────────────────────────
    y += 8;
    setFont('normal', 9, COLOR.gris);
    doc.text('Subtotal:', COL_RIGHT - 50, y);
    setFont('bold', 9, COLOR.grisOscuro);
    doc.text(`$${Number(pago.monto).toLocaleString('es-CO')}`, COL_RIGHT - 5, y, { align: 'right' });

    y += 7;
    setFont('normal', 9, COLOR.gris);
    doc.text('Descuentos:', COL_RIGHT - 50, y);
    setFont('bold', 9, COLOR.grisOscuro);
    doc.text('$0', COL_RIGHT - 5, y, { align: 'right' });

    y += 2;
    line(y, COLOR.verde);
    y += 6;

    // Total grande
    rect(MARGIN + 85, y - 3, COL_RIGHT - MARGIN - 85, 14, COLOR.verdeClaro, 3);
    setFont('bold', 9, COLOR.gris);
    doc.text('TOTAL PAGADO:', COL_RIGHT - 50, y + 5);
    setFont('bold', 14, COLOR.verde);
    doc.text(`$${Number(pago.monto).toLocaleString('es-CO')}`, COL_RIGHT - 5, y + 6, { align: 'right' });

    // ─── FECHA DE VENCIMIENTO DESTACADA ───────────────────────────────────────
    y += 24;

    const nuevaFechaStr = nuevaFechaObj.toLocaleDateString('es-CO', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    rect(MARGIN, y, COL_RIGHT - MARGIN, 20, [255, 249, 230], 4);

    // Icono de calendario (simulado con texto)
    setFont('bold', 18, COLOR.amarillo);
    doc.text(' ', MARGIN + 6, y + 14);

    setFont('bold', 8, [160, 110, 0]);
    doc.text('PRÓXIMA FECHA DE VENCIMIENTO', MARGIN + 18, y + 7);
    setFont('bold', 11, [130, 80, 0]);
    const fechaCapitalized = nuevaFechaStr.charAt(0).toUpperCase() + nuevaFechaStr.slice(1);
    doc.text(fechaCapitalized, MARGIN + 18, y + 15);

    // ─── SECCIÓN TIPO DE PAGO ─────────────────────────────────────────────────
    y += 28;
    setFont('bold', 8, COLOR.gris);
    doc.text('MÉTODO DE PAGO:', MARGIN, y);
    setFont('normal', 8, COLOR.grisOscuro);
    doc.text(pago.tipoPago || 'Transferencia / Comprobante Digital', MARGIN + 38, y);

    y += 6;
    setFont('bold', 8, COLOR.gris);
    doc.text('ESTADO:', MARGIN, y);
    // Badge de estado
    rect(MARGIN + 18, y - 4.5, 28, 6, COLOR.verdeClaro, 2);
    setFont('bold', 8, COLOR.verde);
    doc.text('APROBADO', MARGIN + 21, y);

    // ─── PIE DE PÁGINA ────────────────────────────────────────────────────────
    const footerY = 270;
    line(footerY, COLOR.grisLinea);

    setFont('normal', 7.5, COLOR.gris);
    doc.text(
        '¡Gracias por confiar en nosotros! Este documento es el comprobante oficial de su pago.',
        W / 2, footerY + 6, { align: 'center' }
    );
    doc.text(
        `${JARDIN.nombre}  ·  RUT: ${JARDIN.nit}  ·  Tel: ${JARDIN.telefono}  ·  ${JARDIN.correo}`,
        W / 2, footerY + 12, { align: 'center' }
    );

    setFont('normal', 7, [180, 180, 180]);
    doc.text(`Documento generado el ${new Date().toLocaleString('es-CO')}`, W / 2, footerY + 18, { align: 'center' });

    // ─── RETORNAR BLOB ───────────────────────────────────────────────────────
    return doc.output('blob');
};

/**
 * Genera el nombre del archivo PDF para una factura.
 * Ej: "PedroGomez_2025-01-11.pdf"
 *
 * @param {string} ninoNombre - Nombre del estudiante.
 * @param {Date}   fecha - Fecha de emisión.
 * @returns {string} Nombre de archivo seguro.
 */
export const generarNombreFactura = (ninoNombre, fecha = new Date()) => {
    const nombre = ninoNombre
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')   // quitar tildes
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_]/g, '');

    const fechaStr = fecha.toISOString().split('T')[0]; // YYYY-MM-DD
    return `${nombre}_${fechaStr}.pdf`;
};
